import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';

import Header from 'shared/components/Header';
import PageLoader from 'shared/components/PageLoader';
import PremiumStepper from 'shared/components/PremiumStepper';
import StepNavigation from 'shared/components/StepNavigation';
import StepTransition from 'shared/components/StepTransition';
import { useLanguage } from 'shared/context/LanguageContext';
import { useCourierAuth } from 'features/courier-auth/context/CourierAuthContext';
import {
  fetchTraining,
  finalizeQuizAttempt,
  markVideoComplete,
  startQuizAttempt,
  submitStageAnswers,
  type TrainingView,
} from 'features/courier-training/services/courierTrainingService';
import TrainingResult from 'features/courier-training/components/TrainingResult';
import RetryPrompt from 'features/courier-training/components/RetryPrompt';
import TrainingStagePanel from 'features/courier-training/components/TrainingStagePanel';

const getPreferredStageIndex = (training: TrainingView): number => {
  const firstIncomplete = training.stages.findIndex(
    (stage) => stage.unlocked && !stage.done,
  );
  if (firstIncomplete >= 0) return firstIncomplete;

  const lastUnlocked = training.stages.reduce(
    (result, stage, index) => (stage.unlocked ? index : result),
    0,
  );
  return lastUnlocked;
};

const getPreferredVideoIndex = (
  training: TrainingView,
  stageIndex: number,
): number => {
  const stage = training.stages[stageIndex];
  if (!stage) return 0;
  const firstIncomplete = stage.videos.findIndex((video) => !video.completed);
  return firstIncomplete >= 0 ? firstIncomplete : 0;
};

const CourierTrainingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useCourierAuth();
  const { dir, language } = useLanguage();

  const [training, setTraining] = useState<TrainingView | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [savingVideoId, setSavingVideoId] = useState<string | null>(null);
  const [submittingStageId, setSubmittingStageId] = useState<string | null>(
    null,
  );
  const [failedVideoId, setFailedVideoId] = useState<string | null>(null);
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const contentRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrored(false);
    try {
      let data = await fetchTraining();
      // First-ever visit: create attempt 1 so quiz questions are available.
      if (data.quiz.status === 'NOT_STARTED') {
        data = await startQuizAttempt();
      }
      setTraining(data);
      const preferredStage = getPreferredStageIndex(data);
      setActiveStageIndex(preferredStage);
      setActiveVideoIndex(getPreferredVideoIndex(data, preferredStage));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        navigate('/portal', { replace: true });
        return;
      }
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleVideoEnded = async (videoId: string, alreadyDone: boolean) => {
    if (alreadyDone) return;
    setFailedVideoId(null);
    setSavingVideoId(videoId);
    try {
      const updated = await markVideoComplete(videoId);
      setTraining(updated);
      const stageIndex = updated.stages.findIndex((stage) =>
        stage.videos.some((video) => video.id === videoId),
      );
      const stage = updated.stages[stageIndex];
      const videoIndex = stage?.videos.findIndex(
        (video) => video.id === videoId,
      );

      if (
        stage &&
        videoIndex != null &&
        videoIndex >= 0 &&
        videoIndex < stage.videos.length - 1
      ) {
        setActiveStageIndex(stageIndex);
        setActiveVideoIndex(videoIndex + 1);
      } else if (stage?.done) {
        const nextStageIndex = updated.stages.findIndex(
          (candidate, index) =>
            index > stageIndex && candidate.unlocked && !candidate.done,
        );
        if (nextStageIndex >= 0) {
          setActiveStageIndex(nextStageIndex);
          setActiveVideoIndex(
            getPreferredVideoIndex(updated, nextStageIndex),
          );
        }
      }
    } catch {
      setFailedVideoId(videoId);
    } finally {
      setSavingVideoId(null);
    }
  };

  const handleStageSubmit = async (
    stageId: string,
    answers: Record<string, number>,
  ) => {
    setSubmittingStageId(stageId);
    try {
      const updated = await submitStageAnswers(stageId, answers);
      setTraining(updated);
      const stageIndex = updated.stages.findIndex(
        (stage) => stage.id === stageId,
      );
      const nextStageIndex = updated.stages.findIndex(
        (stage, index) =>
          index > stageIndex && stage.unlocked && !stage.done,
      );
      if (nextStageIndex >= 0) {
        setActiveStageIndex(nextStageIndex);
        setActiveVideoIndex(getPreferredVideoIndex(updated, nextStageIndex));
        window.requestAnimationFrame(() => {
          contentRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      }
    } catch {
      /* leave as-is */
    } finally {
      setSubmittingStageId(null);
    }
  };

  const handleFinalize = async () => {
    setBusy(true);
    try {
      const updated = await finalizeQuizAttempt();
      setTraining(updated);
    } catch {
      /* leave as-is */
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    setBusy(true);
    try {
      const updated = await startQuizAttempt();
      setTraining(updated);
    } catch {
      /* leave as-is */
    } finally {
      setBusy(false);
    }
  };

  const logoutLabel = String(t('portal.logout'));
  const BackArrow = dir === 'rtl' ? ArrowRight : ArrowLeft;

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header onLogout={handleLogout} logoutLabel={logoutLabel} />
      {children}
    </div>
  );

  if (loading) return shell(<PageLoader />);

  if (errored || !training) {
    return shell(
      <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-sm text-gray-600">
          {t('portal.training.unavailable')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="btn-outline inline-flex items-center gap-2"
        >
          <BackArrow size={16} />
          {t('portal.training.backToPortal')}
        </button>
      </main>,
    );
  }

  const backButton = (
    <button
      type="button"
      onClick={() => navigate('/portal')}
      className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-haat-red-700"
    >
      <BackArrow size={16} />
      {t('portal.training.backToPortal')}
    </button>
  );

  // Terminal states.
  if (training.quiz.finished) {
    return shell(
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        {backButton}
        <TrainingResult onContinueToStore={() => navigate('/portal/store')} />
      </main>,
    );
  }

  if (training.quiz.status === 'RETRY_AVAILABLE') {
    return shell(
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        {backButton}
        <RetryPrompt onRetry={handleRetry} starting={busy} />
      </main>,
    );
  }

  const doneStages = training.stages.filter((s) => s.done).length;
  const totalStages = training.stages.length;
  const allDone = doneStages === totalStages;
  const activeStage = training.stages[activeStageIndex] ?? training.stages[0];
  const activeVideo =
    activeStage?.videos[activeVideoIndex] ?? activeStage?.videos[0];
  const maxReached =
    training.stages.reduce(
      (last, stage, index) => (stage.unlocked ? index + 1 : last),
      1,
    );
  const nextStageIndex = training.stages.findIndex(
    (stage, index) => index > activeStageIndex && stage.unlocked,
  );
  const hasNextVideo =
    Boolean(activeStage) &&
    activeVideoIndex < activeStage.videos.length - 1;
  const canGoBack = activeStageIndex > 0 || activeVideoIndex > 0;
  const showFinish = allDone && activeStageIndex === totalStages - 1;
  const hideNext =
    !hasNextVideo && !activeStage?.done && !showFinish;

  const selectStage = (step: number) => {
    const index = step - 1;
    const stage = training.stages[index];
    if (!stage?.unlocked) return;
    setActiveStageIndex(index);
    setActiveVideoIndex(getPreferredVideoIndex(training, index));
    setFailedVideoId(null);
  };

  const goBack = () => {
    setFailedVideoId(null);
    if (activeVideoIndex > 0) {
      setActiveVideoIndex((index) => index - 1);
      return;
    }
    if (activeStageIndex > 0) {
      const previousStageIndex = activeStageIndex - 1;
      const previousStage = training.stages[previousStageIndex];
      setActiveStageIndex(previousStageIndex);
      setActiveVideoIndex(Math.max(previousStage.videos.length - 1, 0));
    }
  };

  const goNext = () => {
    setFailedVideoId(null);
    if (hasNextVideo && activeVideo?.completed) {
      setActiveVideoIndex((index) => index + 1);
      return;
    }
    if (showFinish) {
      handleFinalize();
      return;
    }
    if (activeStage?.done && nextStageIndex >= 0) {
      setActiveStageIndex(nextStageIndex);
      setActiveVideoIndex(getPreferredVideoIndex(training, nextStageIndex));
    }
  };

  return shell(
    <main
      ref={contentRef}
      className="mx-auto w-full max-w-5xl flex-1 scroll-mt-20 px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
    >
      {backButton}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('portal.training.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('portal.training.subtitle')}
        </p>
      </div>

      <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {t('portal.training.progress')}
          </span>
          <span className="text-sm font-bold text-haat-red-700">
            {doneStages}/{totalStages}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <motion.div
            className="h-full bg-[#bf0035]"
            initial={false}
            animate={{
              width: `${
                totalStages > 0 ? (doneStages / totalStages) * 100 : 0
              }%`,
            }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="mb-5 overflow-x-auto rounded-xl border border-gray-200 bg-white px-3 py-4 sm:px-5">
        <div className="min-w-[640px]">
          <PremiumStepper
            current={activeStageIndex + 1}
            maxReached={maxReached}
            total={totalStages}
            trackFromStep={1}
            trackToStep={totalStages}
            stepKeys={training.stages.map(
              (stage) => stage.titleKey.split('.').pop() ?? stage.id,
            )}
            stepLabelNs="portal.training.stages"
            stepperLabelNs="portal.training.stepper"
            onStepSelect={selectStage}
            compact
          />
        </div>
      </div>

      {activeStage ? (
        <StepTransition
          stepKey={activeStageIndex * 100 + activeVideoIndex}
        >
          <TrainingStagePanel
            stage={activeStage}
            stageNumber={activeStageIndex + 1}
            activeVideoIndex={activeVideoIndex}
            language={language}
            savingVideoId={savingVideoId}
            videoSaveFailed={failedVideoId === activeVideo?.id}
            submittingQuiz={submittingStageId === activeStage.id}
            onVideoSelect={(index) => {
              setActiveVideoIndex(index);
              setFailedVideoId(null);
            }}
            onVideoEnded={handleVideoEnded}
            onQuizSubmit={(answers) =>
              handleStageSubmit(activeStage.id, answers)
            }
          />
        </StepTransition>
      ) : null}

      <div className="sticky bottom-0 z-20 mt-5 rounded-t-2xl border border-b-0 border-gray-200 bg-white/95 p-3 shadow-[0_-12px_30px_-20px_rgba(15,23,42,0.45)] backdrop-blur sm:rounded-2xl sm:border-b sm:p-4">
        <StepNavigation
          onBack={goBack}
          onNext={goNext}
          hideBack={!canGoBack}
          hideNext={hideNext}
          nextDisabled={
            hasNextVideo
              ? !activeVideo?.completed
              : !showFinish && (!activeStage?.done || nextStageIndex < 0)
          }
          nextLabel={
            showFinish
              ? String(t('portal.training.finish.action'))
              : hasNextVideo
                ? String(t('portal.training.nextVideo'))
                : String(t('portal.training.nextStage'))
          }
          isSubmitting={busy}
          compact
        />
      </div>
    </main>,
  );
};

export default CourierTrainingPage;
